import React, {useState, useCallback, useEffect} from 'react'

import useSWR from 'swr'
import styled from 'styled-components'
import {Container, media} from 'styled-bootstrap-grid'

import {ProductType, VariantDetail} from '@festi/common/api/rest'
import {useAuth, useEvents} from '@festi/common/contexts'
import {Inventory, Property} from '@festi/common/utils/products'
import {useSingleUserPrice} from '@festi/common/utils/rest'
import {recommendations} from '@festi/common/api/recommend'
import {fluidRange} from '@festi/common/utils/styles'
import {getListingPrice} from '@festi/common/utils/price'
import settings from '@festi/common/constants/settings'
import useTagmanager from '@festi/common/utils/tagmanager'

import {ProductImageGallery, SeeMoreWrapper} from 'src/components/common'
import {
  EnergyImageModal,
  ProductAttributeWrapper,
  ProductHeader,
  ProductInfo,
  ProductPropertiesTable,
  ProductRichText,
  PayLettkaupCalculator,
} from 'src/components/products'
import Demoup from 'src/components/common/Demoup'
import ProductRelated from 'src/components/products/ProductRelated'

const MINIMUM_SIMINN_PAY_PRICE = 20000

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 16px;

  ${media.md`
    display: grid;
    gap: 20px;
    grid-template-columns: calc(50% - 10px) calc(50% - 10px);
    grid-template-areas:
      'gallery info'
      'gallery info'
      'attributes pay'
      'attributes ideal'
      'attributes related'
      'attributes .';
  `}

  ${media.md`
    padding-top: 0;
  `}
`

const ImageGalleryGrid = styled.div`
  grid-area: gallery;
  ${fluidRange('padding-right', '0px', '74px')}
`

const ProductInfoGrid = styled.div`
  grid-area: info;
`

const AttributeGrid = styled.div`
  grid-area: attributes;

  ${media.sm`
    padding-right: 16.67%;
  `}
`

const IdealAccessoriesGrid = styled.div`
  grid-area: ideal;
`

const PayGrid = styled.div`
  grid-area: pay;
`

const RelatedGrid = styled.div`
  grid-area: related;
`

const Relative = styled.div`
  position: relative;
`

const DemoupWrapper = styled.div`
  position: absolute;
  left: 0;
  bottom: 118px;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0 10px;

  ${media.xs`
    bottom: 112px;
  `}

  ${media.md`
    bottom: 128px;
  `}
`

interface Props {
  variant: VariantDetail
  inventory: Inventory[]
  properties: Property[]
  productType: ProductType
}

export default function ProductVariantPage({
  variant,
  inventory,
  properties,
  productType,
}: Props): JSX.Element {
  const {user} = useAuth()
  const {algoliaInit, userToken, sendViewEvent} = useEvents()
  const {sendEcommerceItemViewEvent} = useTagmanager()

  const [showEnergyImageModal, setShowEnergyImageModal] =
    useState<boolean>(false)
  const [showProductDetails, setShowProductDetails] = useState<boolean>(false)

  const activeProduct = !!variant?.listings?.[settings?.channel]?.active

  const {data: userPrice} = useSingleUserPrice(variant?.sku)

  const {data: recommended} = useSWR(
    activeProduct ? [variant?.sku, userToken, 'recommended'] : null,
    () => recommendations(variant?.sku, userToken),
    {
      shouldRetryOnError: false,
    },
  )

  const boughtTogether = recommended?.[0]
  const related = recommended?.[1]

  const {sku, product, energyLabelImages} = variant
  const {description} = product
  const lowestPrice =
    userPrice?.lowestPrice || getListingPrice(variant)?.lowestPrice || 0

  const onToggleEnergyImageModal = useCallback(() => {
    setShowEnergyImageModal(!showEnergyImageModal)
  }, [showEnergyImageModal])

  const onShowProductDetails = useCallback(() => {
    setShowProductDetails(true)
  }, [])

  useEffect(() => {
    sendEcommerceItemViewEvent(variant)
  }, [variant, product])

  useEffect(() => {
    sendViewEvent('viewedObjectIDs', 'Product Viewed', sku)
  }, [sku, algoliaInit, sendViewEvent])

  return (
    <>
      <EnergyImageModal
        image={energyLabelImages[0]?.image?.productGallery2x}
        isOpen={showEnergyImageModal}
        onRequestClose={onToggleEnergyImageModal}
      />

      <ProductHeader />

      <Container>
        <Wrapper>
          <ImageGalleryGrid>
            <Relative>
              <ProductImageGallery variant={variant} />

              <DemoupWrapper>
                <Demoup sku={sku} />
              </DemoupWrapper>
            </Relative>
          </ImageGalleryGrid>

          <ProductInfoGrid>
            <ProductInfo
              user={user}
              variant={variant}
              inventory={inventory}
              userPrice={userPrice}
              onShowProductDetails={onShowProductDetails}
              onToggleEnergyImageModal={onToggleEnergyImageModal}
            />
          </ProductInfoGrid>

          <IdealAccessoriesGrid>
            <ProductRelated
              list="AR Bought Together"
              title="Oft keypt með"
              variants={boughtTogether}
            />
          </IdealAccessoriesGrid>

          <AttributeGrid>
            {!!description && (
              <ProductAttributeWrapper label="Upplýsingar">
                <SeeMoreWrapper>
                  <ProductRichText html={description} />
                </SeeMoreWrapper>
              </ProductAttributeWrapper>
            )}

            {!!productType && !!properties?.length && (
              <ProductAttributeWrapper label="Eiginleikar">
                <SeeMoreWrapper
                  scrollIntoView={showProductDetails}
                  setScrollIntoView={setShowProductDetails}
                >
                  <ProductPropertiesTable
                    title={productType.name}
                    items={properties}
                  />
                </SeeMoreWrapper>
              </ProductAttributeWrapper>
            )}
          </AttributeGrid>

          <PayGrid>
            {lowestPrice >= MINIMUM_SIMINN_PAY_PRICE && activeProduct && (
              <PayLettkaupCalculator sku={sku} price={lowestPrice} />
            )}
          </PayGrid>

          <RelatedGrid>
            <ProductRelated
              list="AR Related Products"
              title="Sambærilegar vörur"
              variants={related}
            />
          </RelatedGrid>
        </Wrapper>
      </Container>
    </>
  )
}
